/**
 * PayPal Configuration Tab
 *
 * Admin section to configure PayPal settings for payments.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Input } from '../../components/ui';
import {
  paypalConfigService,
  type UpdatePaypalConfigRequest,
} from '../../services/paypal-config.service';

// Type for tracking original values (client_secret excluded since it's never returned from API)
interface OriginalConfig {
  singlePaymentPrice: number;
  monthlySubscriptionPrice: number;
  clientId: string;
  planId: string;
  webhookId: string;
}

const PaypalConfigTab = () => {
  const { t } = useTranslation();

  // Form state
  const [originalConfig, setOriginalConfig] = useState<OriginalConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form fields
  const [singlePaymentPrice, setSinglePaymentPrice] = useState(0.49);
  const [monthlySubscriptionPrice, setMonthlySubscriptionPrice] = useState(0.35);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [planId, setPlanId] = useState('');
  const [webhookId, setWebhookId] = useState('');

  // Check if form has changes compared to original config
  const hasChanges = (): boolean => {
    // If client secret is filled, there's a change
    if (clientSecret) return true;

    // If no original config, check if any field has value
    if (!originalConfig) {
      return !!(clientId || planId || webhookId);
    }

    // Compare current values with original
    return (
      singlePaymentPrice !== originalConfig.singlePaymentPrice ||
      monthlySubscriptionPrice !== originalConfig.monthlySubscriptionPrice ||
      clientId !== originalConfig.clientId ||
      planId !== originalConfig.planId ||
      webhookId !== originalConfig.webhookId
    );
  };

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[PaypalConfigTab] Fetching config...');
      const response = await paypalConfigService.get();
      console.log('[PaypalConfigTab] Response:', response);

      if (response.success && response.data) {
        const data = response.data;

        // Set form values
        const singlePaymentPriceValue = data.singlePaymentPrice ?? 0.49;
        const monthlySubscriptionPriceValue = data.monthlySubscriptionPrice ?? 0.35;
        const clientIdValue = data.clientId || '';
        const clientSecretValue = data.clientSecret || '';
        const planIdValue = data.planId || '';
        const webhookIdValue = data.webhookId || '';

        setSinglePaymentPrice(singlePaymentPriceValue);
        setMonthlySubscriptionPrice(monthlySubscriptionPriceValue);
        setClientId(clientIdValue);
        setClientSecret(clientSecretValue);
        setPlanId(planIdValue);
        setWebhookId(webhookIdValue);

        // Store original values for change detection
        setOriginalConfig({
          singlePaymentPrice: singlePaymentPriceValue,
          monthlySubscriptionPrice: monthlySubscriptionPriceValue,
          clientId: clientIdValue,
          planId: planIdValue,
          webhookId: webhookIdValue,
        });
      } else if (!response.success) {
        setError(
          response.error?.message ||
            t('admin.paypal.errors.loadFailed', 'Erreur lors du chargement de la configuration')
        );
      }
    } catch (err) {
      console.error('[PaypalConfigTab] Error:', err);
      setError(t('admin.paypal.errors.loadFailed', 'Erreur lors du chargement de la configuration'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsSaving(true);

    const updateData: UpdatePaypalConfigRequest = {
      singlePaymentPrice,
      monthlySubscriptionPrice,
      clientId: clientId.trim() || null,
      clientSecret: clientSecret.trim() || null,
      planId: planId.trim() || null,
      webhookId: webhookId.trim() || null,
    };

    try {
      const response = await paypalConfigService.update(updateData);

      if (response.success) {
        setSuccess(true);
        setClientSecret(''); // Clear secret field after save

        // Update original config to reflect saved values
        setOriginalConfig({
          singlePaymentPrice,
          monthlySubscriptionPrice,
          clientId: clientId.trim(),
          planId: planId.trim(),
          webhookId: webhookId.trim(),
        });
      } else {
        setError(
          response.error?.message ||
            t('admin.paypal.errors.saveFailed', 'Erreur lors de la sauvegarde')
        );
      }
    } catch {
      setError(t('admin.paypal.errors.saveFailed', 'Erreur lors de la sauvegarde'));
    } finally {
      setIsSaving(false);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-48 animate-pulse" />
        <div className="h-96 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('admin.paypal.title', 'Configuration PayPal')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t(
            'admin.paypal.description',
            'Configurez les paramètres PayPal pour les paiements uniques et les abonnements.'
          )}
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {t('admin.paypal.saveSuccess', 'Configuration PayPal enregistrée avec succès.')}
        </div>
      )}

      {/* Configuration form */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Pricing settings */}
          <div>
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.paypal.sections.pricing', 'Tarification')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('admin.paypal.fields.singlePaymentPrice', 'Prix paiement unique (€)')}
                type="number"
                value={singlePaymentPrice}
                onChange={(e) => setSinglePaymentPrice(parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                disabled={isSaving}
              />
              <Input
                label={t(
                  'admin.paypal.fields.monthlySubscriptionPrice',
                  'Prix abonnement mensuel (€)'
                )}
                type="number"
                value={monthlySubscriptionPrice}
                onChange={(e) => setMonthlySubscriptionPrice(parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* API Credentials */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.paypal.sections.credentials', 'Identifiants API')}
            </h3>
            <div className="space-y-4">
              <Input
                label={t('admin.paypal.fields.clientId', 'PayPal Client ID')}
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="AaBbCcDdEeFf..."
                disabled={isSaving}
                autoComplete="off"
              />
              <Input
                label={t('admin.paypal.fields.clientSecret', 'PayPal Client Secret')}
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                disabled={isSaving}
                autoComplete="new-password"
                helperText={t(
                  'admin.paypal.fields.clientSecretHelper',
                  'Secret de votre application PayPal (requis pour annuler les abonnements).'
                )}
              />
            </div>
          </div>

          {/* Subscription settings */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.paypal.sections.subscription', 'Abonnement récurrent')}
            </h3>
            <div className="space-y-4">
              <Input
                label={t('admin.paypal.fields.planId', 'PayPal Plan ID')}
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                placeholder="P-XXXXXXXXXXXXXXXXXXXXXXXX"
                disabled={isSaving}
                helperText={t(
                  'admin.paypal.fields.planIdHelper',
                  "ID du plan d'abonnement créé dans PayPal Dashboard."
                )}
              />
              <Input
                label={t('admin.paypal.fields.webhookId', 'PayPal Webhook ID (optionnel)')}
                value={webhookId}
                onChange={(e) => setWebhookId(e.target.value)}
                placeholder="WH-XXXXXXXXXXXXXXXXXXXXXXXX"
                disabled={isSaving}
                helperText={t(
                  'admin.paypal.fields.webhookIdHelper',
                  'ID du webhook pour recevoir les notifications PayPal.'
                )}
              />
            </div>
          </div>

          {/* Submit button */}
          <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button type="submit" disabled={isSaving || !hasChanges()}>
              {isSaving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default PaypalConfigTab;
